<template>
  <v-container>
    <v-btn
      text
      small
      :to="{ name: 'admin-report-metrics' }"
      class="mb-2"
    >
      <v-icon
        left
        small
      >
        {{ icons.mdiArrowLeft }}
      </v-icon>
      指標管理へ戻る
    </v-btn>

    <h1>指標アイテム管理</h1>
    <p
      v-if="metricId"
      class="text--secondary"
    >
      指標ID: {{ metricId }}
    </p>

    <v-skeleton-loader
      v-if="loading"
      type="table"
    />

    <div v-else>
      <v-data-table
        :headers="headers"
        :items="items"
        :footer-props="{ itemsPerPageText: '行/ページ:' }"
        no-data-text="データがありません"
      >
        <template #top>
          <v-toolbar
            flat
            color="transparent"
          >
            <v-spacer />
            <v-btn
              color="primary"
              :ripple="false"
              small
              @click="openCreateDialog"
            >
              新規作成
            </v-btn>
          </v-toolbar>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            :ripple="false"
            @click="openEditDialog(item)"
          >
            <v-icon small>
              {{ icons.mdiPencilOutline }}
            </v-icon>
          </v-btn>
          <v-dialog max-width="400">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                :ripple="false"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>
                  {{ icons.mdiTrashCanOutline }}
                </v-icon>
              </v-btn>
            </template>
            <template #default="dialog">
              <v-card>
                <v-card-title>削除の確認</v-card-title>
                <v-card-text>
                  この指標アイテムを削除しますか？この操作は取り消せません。
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    small
                    @click="dialog.value = false"
                  >
                    キャンセル
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    @click="destroyItem(item.id); dialog.value = false"
                  >
                    削除
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </template>
      </v-data-table>
    </div>

    <v-dialog
      v-model="editDialog"
      max-width="500"
      @click:outside="resetParams"
    >
      <template #default="dialog">
        <v-card>
          <v-card-title>{{ isNew ? '新規作成' : '編集' }}</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="params.reportFormulaItemId"
                  :items="formulaItems"
                  label="計測項目"
                  item-text="attributes.label"
                  item-value="id"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="params.sign"
                  :items="signOptions"
                  label="符号"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="params.coefficient"
                  label="係数"
                  type="number"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              small
              @click="closeDialog(dialog)"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="!params.reportFormulaItemId"
              @click="saveItem(dialog)"
            >
              {{ isNew ? '作成' : '更新' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  onMounted,
} from '@vue/composition-api'
import { mdiPencilOutline, mdiTrashCanOutline, mdiArrowLeft } from '@mdi/js'
import { useRouter } from '@core/utils'
import ReportMetricItemApi from '@/api/v1/admin/ReportMetricItem'
import ReportFormulaItemApi from '@/api/v1/admin/ReportFormulaItem'

export default {
  setup() {
    const { route } = useRouter()
    const metricId = computed(() => route.value.query.metric_id)

    const items = ref([])
    const formulaItems = ref([])
    const loading = ref(false)
    const editDialog = ref(false)
    const params = reactive({
      id: null,
      reportFormulaItemId: null,
      sign: '+',
      coefficient: '1.0',
    })

    const isNew = computed(() => params.id === null)

    const signOptions = [
      { text: '+', value: '+' },
      { text: '-', value: '-' },
    ]

    const headers = [
      { text: '計測項目', value: 'attributes.formulaItemLabel' },
      { text: '符号', value: 'attributes.sign' },
      { text: '係数', value: 'attributes.coefficient' },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ]

    const fetchItems = async () => {
      if (!metricId.value) return
      loading.value = true
      try {
        const [itemsRes, formulaRes] = await Promise.all([
          ReportMetricItemApi.getList(metricId.value),
          ReportFormulaItemApi.getList(),
        ])
        items.value = itemsRes.data.reportMetricItems?.data || []
        formulaItems.value = formulaRes.data.reportFormulaItems?.data || []
      } catch (e) {
        // TODO
      } finally {
        loading.value = false
      }
    }

    const resetParams = () => {
      params.id = null
      params.reportFormulaItemId = null
      params.sign = '+'
      params.coefficient = '1.0'
    }

    const openCreateDialog = () => {
      resetParams()
      editDialog.value = true
    }

    const openEditDialog = item => {
      params.id = item.id
      params.reportFormulaItemId = item.attributes.reportFormulaItemId
      params.sign = item.attributes.sign
      params.coefficient = item.attributes.coefficient
      editDialog.value = true
    }

    const closeDialog = dialog => {
      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      resetParams()
    }

    const saveItem = async dialog => {
      try {
        const payload = {
          report_formula_item_id: params.reportFormulaItemId,
          sign: params.sign,
          coefficient: params.coefficient,
        }
        if (isNew.value) {
          await ReportMetricItemApi.create(metricId.value, payload)
        } else {
          await ReportMetricItemApi.update(metricId.value, params.id, payload)
        }
        await fetchItems()
        closeDialog(dialog)
      } catch (e) {
        // TODO
      }
    }

    const destroyItem = async id => {
      try {
        await ReportMetricItemApi.destroy(metricId.value, id)
        await fetchItems()
      } catch (e) {
        // TODO
      }
    }

    onMounted(fetchItems)

    return {
      metricId,
      items,
      formulaItems,
      loading,
      editDialog,
      params,
      isNew,
      headers,
      signOptions,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      saveItem,
      destroyItem,
      resetParams,
      icons: {
        mdiPencilOutline,
        mdiTrashCanOutline,
        mdiArrowLeft,
      },
    }
  },
}
</script>
